import { useCallback, useMemo, useState } from "react";
import {
  Anchor,
  Badge,
  Center,
  Group,
  MultiSelect,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  ReportDateRangePicker,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import { useWorkloadReport } from "../../hooks/useReports";
import { usePathologists, useFacilities } from "../../hooks/useReferenceData";
import {
  reportsService,
  type WorkflowStage,
  type WorkloadParams,
  type WorkloadUserRow,
} from "../../services/reports.service";
import { getLabTodayDateOnly } from "../../utils/dateOnly";
import {
  getDefaultWorkloadDateRange,
  workloadRequestDateRange,
} from "./workloadDateRange";

const STAGES: { value: WorkflowStage; label: string }[] = [
  { value: "accessioning", label: "Accessioning" },
  { value: "grossing", label: "Grossing" },
  { value: "embedding", label: "Embedding" },
  { value: "cutting", label: "Cutting" },
  { value: "distribution", label: "Distribution" },
  { value: "pathologist_review", label: "Pathologist Review" },
];

interface StageColumn {
  readonly key: keyof WorkloadUserRow;
  readonly label: string;
}

interface SummaryValue extends StageColumn {
  readonly caption: string | null;
  readonly display: number | string;
}

interface RatePresentation {
  readonly color: string;
  readonly tooltip: string;
}

const STAGE_COLUMNS: Record<WorkflowStage, StageColumn[]> = {
  accessioning: [
    { key: "casesAccessioned", label: "Cases" },
    { key: "specimensAccessioned", label: "Specimens" },
    { key: "avgAccessioningMinutes", label: "Avg Time (min)" },
    { key: "accessioningRatePerHour", label: "Parts/Hour" },
  ],
  grossing: [
    { key: "casesGrossed", label: "Cases" },
    { key: "specimensGrossed", label: "Specimens" },
    { key: "avgGrossingMinutes", label: "Avg Time (min)" },
    { key: "medianGrossingMinutes", label: "Median Time (min)" },
  ],
  embedding: [
    { key: "casesEmbedded", label: "Cases" },
    { key: "specimensEmbedded", label: "Specimens" },
    { key: "blocksEmbedded", label: "Blocks" },
    { key: "avgEmbeddingMinutes", label: "Avg Time (min)" },
    { key: "medianEmbeddingMinutes", label: "Median Time (min)" },
  ],
  cutting: [
    { key: "casesCut", label: "Cases" },
    { key: "blocksCut", label: "Blocks" },
    { key: "slidesCut", label: "Slides" },
  ],
  distribution: [
    { key: "casesDistributed", label: "Cases" },
    { key: "slidesDistributed", label: "Slides" },
  ],
  pathologist_review: [
    { key: "casesSigned", label: "Cases Signed" },
    { key: "slidesReviewed", label: "Slides" },
    { key: "avgTatDays", label: "TAT (days)" },
    { key: "medianTatDays", label: "Median TAT (days)" },
    { key: "avgReviewMinutes", label: "Distribution → Sign-out (min)" },
    { key: "medianReviewMinutes", label: "Median Dist → Sign-out (min)" },
    { key: "cptCodesAssigned", label: "CPT Codes" },
  ],
};

const AVG_KEYS = new Set<keyof WorkloadUserRow>([
  "avgAccessioningMinutes",
  "accessioningRatePerHour",
  "avgGrossingMinutes",
  "avgEmbeddingMinutes",
  "avgReviewMinutes",
  "avgTatDays",
]);

const MEDIAN_KEYS = new Set<keyof WorkloadUserRow>([
  "medianGrossingMinutes",
  "medianEmbeddingMinutes",
  "medianReviewMinutes",
  "medianTatDays",
]);

const DEFAULT_RANGE: DateRange = getDefaultWorkloadDateRange();

export function WorkloadReportContent() {
  const [stage, setStage] = useState<WorkflowStage>("grossing");
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { data: pathologists = [] } = usePathologists();
  const { data: facilities = [] } = useFacilities();

  const userOptions = useMemo(
    () =>
      pathologists.map((pathologist) => ({
        value: pathologist.id,
        label: pathologist.name,
      })),
    [pathologists]
  );
  const facilityOptions = useMemo(
    () =>
      facilities.map((facility) => ({
        value: facility.id,
        label: facility.name,
      })),
    [facilities]
  );
  const requestRange = useMemo(
    () => workloadRequestDateRange(dateRange),
    [dateRange]
  );
  const params = useMemo<WorkloadParams>(
    () => ({
      stage,
      ...requestRange,
      userIds: userIds.length > 0 ? userIds.join(",") : undefined,
      facilityId: facilityId ?? undefined,
    }),
    [stage, requestRange, userIds, facilityId]
  );
  const { data, isLoading } = useWorkloadReport(params, page);
  const rows = useMemo(() => data?.data ?? [], [data]);
  const columns = useMemo(() => STAGE_COLUMNS[stage], [stage]);
  const totalPages = useMemo(
    () => (data ? Math.ceil(data.pagination.total / data.pagination.limit) : 1),
    [data]
  );
  const summaryValues = useMemo(
    () => columns.map((column) => summarizeColumn(column, rows)),
    [columns, rows]
  );
  const cptBreakdownLinks = useMemo(
    () =>
      new Map(
        rows.map((row) => [
          row.userId,
          cptBreakdownUrl(
            row.userId,
            requestRange.startDate,
            requestRange.endDate
          ),
        ])
      ),
    [requestRange, rows]
  );
  const ratePresentations = useMemo(
    () =>
      new Map(
        rows.map((row) => [
          row.userId,
          accessioningRatePresentation(row.accessioningRatePerHour),
        ])
      ),
    [rows]
  );
  const exportFilename = useMemo(
    () => `workload-${stage}-${getLabTodayDateOnly()}.csv`,
    [stage]
  );

  const handleStageChange = useCallback((value: string | null) => {
    if (!value) return;
    setStage(value as WorkflowStage);
    setPage(1);
  }, []);
  const handleUserIdsChange = useCallback((value: string[]) => {
    setUserIds(value);
    setPage(1);
  }, []);
  const handleFacilityChange = useCallback((value: string | null) => {
    setFacilityId(value);
    setPage(1);
  }, []);
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);
  const handlePageChange = useCallback((value: number) => setPage(value), []);
  const handleExport = useCallback(
    () => reportsService.exportWorkload(params),
    [params]
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Select
              label="Workflow Stage"
              data={STAGES}
              value={stage}
              onChange={handleStageChange}
              clearable={false}
              allowDeselect={false}
              w={220}
            />
            <MultiSelect
              label="Users"
              placeholder="All users"
              data={userOptions}
              value={userIds}
              onChange={handleUserIdsChange}
              searchable
              clearable
              w={240}
            />
            <Select
              label="Facility"
              placeholder="All facilities"
              data={facilityOptions}
              value={facilityId}
              onChange={handleFacilityChange}
              searchable
              clearable
              w={200}
            />
            <ReportDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </Group>
          <ReportExportButton
            filename={exportFilename}
            fetchData={handleExport}
          />
        </Group>
      </Paper>

      {data && (
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {summaryValues.map((summary) => (
            <Paper key={summary.key} p="sm" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                {summary.label}
              </Text>
              <Text size="xl" fw={700}>
                {summary.display}
              </Text>
              {summary.caption && (
                <Text size="xs" c="dimmed">
                  {summary.caption}
                </Text>
              )}
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} h={36} />
            ))}
          </Stack>
        ) : rows.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">No data for the selected filters.</Text>
          </Center>
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  {columns.map((column) => (
                    <Table.Th key={column.key}>{column.label}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.userId}>
                    <Table.Td>
                      <Badge variant="light" color="blue">
                        {row.userName}
                      </Badge>
                    </Table.Td>
                    {columns.map((column) => (
                      <Table.Td key={column.key}>
                        {column.key === "cptCodesAssigned" ? (
                          <Group gap="xs" wrap="nowrap">
                            <Text component="span">
                              {row.cptCodesAssigned ?? 0}
                            </Text>
                            <Anchor
                              component={Link}
                              to={
                                cptBreakdownLinks.get(row.userId) ??
                                "/reports/cpt-frequency"
                              }
                              size="sm"
                            >
                              Breakdown
                            </Anchor>
                          </Group>
                        ) : column.key === "accessioningRatePerHour" &&
                          row.accessioningRatePerHour != null ? (
                          <Tooltip
                            label={
                              ratePresentations.get(row.userId)?.tooltip ?? ""
                            }
                          >
                            <Badge
                              variant="filled"
                              color={
                                ratePresentations.get(row.userId)?.color ??
                                "gray"
                              }
                              size="md"
                            >
                              {row.accessioningRatePerHour} / hr
                            </Badge>
                          </Tooltip>
                        ) : (
                          row[column.key] ?? "—"
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={handlePageChange}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}

function summarizeColumn(
  column: StageColumn,
  rows: WorkloadUserRow[]
): SummaryValue {
  const values = rows
    .map((row) => row[column.key])
    .filter((value): value is number => typeof value === "number");
  const total = values.reduce((sum, value) => sum + value, 0);
  const average = values.length > 0 ? Math.round(total / values.length) : 0;
  if (MEDIAN_KEYS.has(column.key)) {
    return { ...column, display: "—", caption: "per-user medians" };
  }
  if (AVG_KEYS.has(column.key)) {
    return { ...column, display: average, caption: "avg across users" };
  }
  return { ...column, display: total, caption: null };
}

function cptBreakdownUrl(
  userId: string,
  startDate: string,
  endDate: string
): string {
  const search = new URLSearchParams({
    pathologistId: userId,
    startDate,
    endDate,
  });
  return `/reports/cpt-frequency?${search.toString()}`;
}

function accessioningRatePresentation(rate?: number | null): RatePresentation {
  if (rate == null) return { color: "gray", tooltip: "" };
  if (rate >= 30 && rate <= 40) {
    return { color: "green", tooltip: "Meeting Target (30-40 parts/hour)" };
  }
  if (rate < 30) {
    return { color: "orange", tooltip: "Below Target (<30 parts/hour)" };
  }
  return { color: "teal", tooltip: "Above Target (>40 parts/hour)" };
}
